import React, { ReactNode } from 'react';
import { Page } from '../../components/atoms/layout/Page';
import { observer, Provider } from 'mobx-react';
import { observable, makeObservable } from 'mobx';
import { CourseShowPageState } from './states/CourseShowPageState';
import { CourseHeader } from '../../components/molecules/courses/show/CourseHeader';
import { CourseLessonList } from '../../components/molecules/courses/show/CourseLessonList';
import { CourseLessonItem } from '../../components/molecules/courses/show/CourseLessonItem';
import { NotFoundPage } from '../../../mainv2/containers/common/NotFoundPage';
import { SectionDetailsPane } from '../../components/organisms/sections/SectionDetailsPane';
import { parseQueryParams, RouteLocation } from '../../types/location';
import { V3Routes } from '../../routes';
import { Confirmation } from '../../components/molecules/modals/Confirmation';
import { OverlayPage } from '../../../commonv3/components/molecules/layout/OverlayPage';
import { Route, Switch } from 'react-router';
import { GlobalStatesV3 } from '../../../commonv3/states/GlobalStatesV3';
import { LessonDetailsPage } from '../../components/organisms/lessons/LessonDetailsPage';
import { TokenType } from '../../../commonv3/types/tokens';
import moment from 'moment-timezone';
import { Alert } from '../../components/molecules/modals/Alert';
import { LoaderRow } from '../../components/atoms/layout/LoaderRow';
import { withNavigation } from '../../components/hocs/withNavigation';
import { NavigationSpecs } from '../../states/NavigationState';
import { authenticated } from '../../components/hocs/authenticated';
import { CourseExamList, CourseExamListItem } from '../../components/molecules/courses/show/CourseExamList';
import { useCurrentUserState } from '../../../commonv3/hooks/useCurrentUserState';
import { CourseStepForExam } from '../../components/molecules/courses/show/CourseStepForExam';
import { BreadCrumb } from '../breadcrumb/BreadCrumb';
import { CoursePage } from '../../components/atoms/layout/course_page/CoursePage';
import { CoursePageHeader } from '../../components/atoms/layout/course_page/CoursePageHeader';
import { CoursePageBody } from '../../components/atoms/layout/course_page/CoursePageBody';

type LayoutProps = {
  children: ReactNode;
};

export const Layout = observer(({ children }: LayoutProps) => {
  if (GlobalStatesV3.viewport.isMobile) {
    return (
      <OverlayPage open={true} onRequestClose={() => null}>
        {children}
      </OverlayPage>
    );
  } else {
    return <>{children}</>;
  }
});

export type Props = {
  match: {
    url: string;
    params: {
      courseId: string;
    };
  };
  location: RouteLocation;
};

export const CourseShowPage = withNavigation(NavigationSpecs.side())(
  authenticated(
    observer(
      class CourseShowPage extends React.Component<Props> {
        preview = false;
        pageState = new CourseShowPageState();

        constructor(props: Props) {
          super(props);

          makeObservable(this, {
            preview: observable,
            pageState: observable,
          });
        }

        async componentDidMount() {
          const { courseId } = this.props.match.params;
          const query = parseQueryParams(this.props.location);
          this.preview = query['preview'] === 'true';
          this.pageState.init(Number(courseId), this.preview);
        }

        render() {
          const {
            course: courseResource,
            lessonStates,
            loading,
            preview,
            courseId,
            selectedLessonId,
            lessonActivationDialog,
            startLesson,
            activateAndStart,
            startExam,
            startExamSection,
          } = this.pageState;
          const {
            location,
            match: { url },
          } = this.props;
          if (loading) {
            return (
              <Layout>
                <Page>
                  <LoaderRow />
                </Page>
              </Layout>
            );
          }
          if (courseResource.isNotFound()) {
            return <NotFoundPage />;
          }
          const course = courseResource.get();
          const lessonToken = GlobalStatesV3.currentUser.availableTokens.findAffordableToken(
            TokenType.ONLINE_LESSON,
            1,
            moment()
          );
          const userState = useCurrentUserState();
          // 実技試験合格に向けたステップ表示の有無を判定
          // 検証のためWebデザインコースにのみ表示するので今はid指定で判定しているが、今後は管理画面から入稿できるようにし、自動で判定させたい
          const showStepForExam = course.id === 42;
          const breadCrumbItems = [{ text: 'コース', to: V3Routes.courses() }, { text: course.title }];
          return (
            <Layout>
              <CoursePage id="course-show-page">
                <CoursePageHeader>
                  <BreadCrumb items={breadCrumbItems} hasHome />
                  <CourseHeader course={course} />
                </CoursePageHeader>
                <CoursePageBody>
                  <CourseLessonList>
                    {lessonStates.map((lesson, index) => (
                      <CourseLessonItem
                        key={lesson.lesson.id}
                        index={index}
                        lesson={lesson}
                        onLessonStart={startLesson}
                        onSectionClick={(sectionId) =>
                          GlobalStatesV3.routing.push(V3Routes.courseSection(courseId, lesson.lesson.id, sectionId))
                        }
                      />
                    ))}
                  </CourseLessonList>
                  {showStepForExam && <CourseStepForExam />}
                  {course.exams.length > 0 && (
                    <CourseExamList>
                      {course.exams.map((exam) => (
                        <CourseExamListItem
                          key={exam.id}
                          exam={exam}
                          course={course}
                          onStartClick={() => startExam(exam)}
                          onSectionClick={startExamSection}
                        />
                      ))}
                    </CourseExamList>
                  )}

                  <OverlayPage
                    open={location.pathname.includes('lessons')}
                    onRequestClose={() => GlobalStatesV3.routing.push(V3Routes.course(courseId, preview))}
                  >
                    <Provider courseState={this.pageState}>
                      <Switch>
                        <Route exact path={`${url}/lessons/:lessonId`} component={LessonDetailsPage} />
                        <Route
                          exact
                          path={`${url}/lessons/:lessonId/sections/:sectionId`}
                          component={SectionDetailsPane}
                        />
                      </Switch>
                    </Provider>
                  </OverlayPage>

                  {lessonToken && (
                    <Confirmation
                      headerMessage="このレッスンを閲覧しますか？"
                      message={lessonToken.amount >= 0 ? `残り${lessonToken.amount}回` : '無制限'}
                      confirmationText="レッスンを始める"
                      open={lessonActivationDialog.isOpen}
                      onAbort={lessonActivationDialog.close}
                      onConfirmation={() => activateAndStart(selectedLessonId)}
                    />
                  )}
                  {!lessonToken && (
                    <Alert
                      headerMessage="閲覧にはプランのアップグレードが必要です。"
                      open={lessonActivationDialog.isOpen}
                      onAbort={lessonActivationDialog.close}
                    />
                  )}
                </CoursePageBody>
              </CoursePage>
            </Layout>
          );
        }
      }
    )
  )
);
